import { TRANSACTION_MAP } from "@constants/transaction";
import { api } from "@lib/orpc-client";
import { extractData, getDisbursementSummary } from "@lib/utils";
import { RedeemTransactionDetail } from "../types/redemption";
import useSWR from "swr";
import * as Sentry from "@sentry/react";

type Params = {
  uuid?: string;
  transaction_uuid?: string;
  code: number;
  fetchImmediately?: boolean;
};

const useActivityDetail = ({
  uuid,
  transaction_uuid,
  code,
  fetchImmediately = false,
}: Params) => {
  const isLegacyTransaction = transaction_uuid !== undefined;

  // Create the key that would be used for fetching
  const fetchKey = isLegacyTransaction
    ? `wallet.transactions.detail.${transaction_uuid}`
    : `walletV2.activities.detail.${uuid}`;

  // Define the fetcher function outside of useSWR for reuse
  const fetcher = async () => {
    try {
      const response = isLegacyTransaction
        ? await api.wallet.transactions.detail({ uuid: transaction_uuid! })
        : await api.walletV2.activities.detail({ activity_uuid: uuid! });
      const isMultipartDisbursement = (response as any).is_multipart;
      const activityDetail = extractData(
        response,
        isLegacyTransaction ? "old" : "new",
        code,
        TRANSACTION_MAP
      );
      if (isMultipartDisbursement) {
        activityDetail.additionalDetail = getDisbursementSummary(
          response as RedeemTransactionDetail
        );
      }
      return activityDetail;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  // Use SWR with conditional fetching
  const { data, isLoading, error, mutate } = useSWR(
    fetchImmediately ? fetchKey : null,
    fetcher
  );

  const refetch = async () => {
    try {
      // Manually fetch the data
      const newData = await fetcher();

      // Update the cache with the new data
      await mutate(newData, false);

      return newData;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  };

  return {
    data: data,
    isLoading,
    error,
    refetch,
  };
};

export default useActivityDetail;
